import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChainActionDialog from "@/components/ChainActionDialog";
import { RequestDetailsLoader } from "@/components/RequestDetailsPanel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, HandCoins, XCircle, BadgeCheck, CreditCard, CheckCheck } from "lucide-react";
import InvoiceUploader, { InvoiceLinks } from "@/components/InvoiceUploader";
import { ChainButton } from "@/components/ApprovalChain";
import AdvanceSettlementForm from "@/components/AdvanceSettlementForm";
import AdvanceEditForm from "@/components/AdvanceEditForm";
import { RejectedNote } from "@/components/EditResubmit";
import { DisburseForm, DisbursementCorrectForm } from "@/components/DisburseDialog";

const OPEN_ADV = ["Requested", "PMEndorsed", "Authorized", "AccountsVerified"];

const statusColor = {
  Requested: "bg-amber-100 text-amber-700",
  Approved: "bg-blue-100 text-blue-700",
  Disbursed: "bg-cyan-100 text-cyan-700",
  PartiallySettled: "bg-indigo-100 text-indigo-700",
  Settled: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-red-100 text-red-700",
};

export default function Advances() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [payees, setPayees] = useState([]);
  const [open, setOpen] = useState(false);
  const [settleOn, setSettleOn] = useState(null);
  const [disburseOn, setDisburseOn] = useState(null);
  const [correctOn, setCorrectOn] = useState(null);
  const [target, setTarget] = useState(null);

  const isApprover = ["Admin", "Approver", "Authorizer", "Accounts", "ProjectManager", "Trustee"].includes(user?.role);
  const isAccounts = ["Admin", "Accounts"].includes(user?.role);

  const [selfSettle, setSelfSettle] = useState(null);
  const [editAdv, setEditAdv] = useState(null);
  const closeEdit = () => { setEditAdv(null); window.history.replaceState(null, "", "/app/advances"); };
  const load = async () => {
    const data = (await api.get("/advances")).data;
    setItems(data);
    return data;
  };
  useEffect(() => {
    Promise.all([api.get("/projects"), api.get("/masters/payees")]).then(([p, pa]) => {
      setProjects(p.data); setPayees(pa.data);
    });
    load().then(data => {
      const sid = new URLSearchParams(window.location.search).get("settle");
      const a = sid && data.find(x => x.id === sid && x.requester_id === user?.id);
      if (a) setSelfSettle(a);
      const qs = new URLSearchParams(window.location.search);
      const e = data.find(x => x.id === qs.get("edit") && x.status === "SentBack" && x.requester_id === user?.id);
      const c = data.find(x => x.id === qs.get("copy") && x.status === "Rejected" && x.requester_id === user?.id);
      if (e) setEditAdv({ adv: e, mode: "edit" });
      else if (c) setEditAdv({ adv: c, mode: "copy" });
    });
  }, []);
  const confirmRefund = async (a, s) => {
    try { await api.post(`/advances/${a.id}/settlements/${s.id}/confirm-refund`); toast.success("Refund confirmed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openAdv = items.filter(i => ["Disbursed", "PartiallySettled"].includes(i.status));
  const settleActions = (a) => (
    <div className="flex flex-col items-end gap-1">
      {a.requester_id === user?.id
        ? <Button size="sm" onClick={() => setSelfSettle(a)} data-testid={`adv-settle-${a.id}`}><CheckCheck className="w-3.5 h-3.5 mr-1" />Settle</Button>
        : <span className="text-xs text-slate-500">Awaiting settlement by {a.requester_name}</span>}
      {(a.settlements || []).filter(s => s.refund_amount > 0 && !s.refund_confirmed).map(s => (
        isAccounts && a.requester_id !== user?.id
          ? <Button key={s.id} size="sm" variant="outline" onClick={() => confirmRefund(a, s)} data-testid={`adv-confirm-refund-${s.id}`}>Confirm refund ₹{s.refund_amount} ({s.refund_utr})</Button>
          : <span key={s.id} className="text-[11px] text-amber-700">Refund ₹{s.refund_amount} awaiting Accounts confirmation</span>
      ))}
      {(a.settlements || []).filter(s => s.status === "Submitted").map(s => <span key={s.id} className="text-[11px] text-slate-500">Bills {(s.bills || []).map(b => b.rmb_id).join(", ")} in approval</span>)}
      {isAccounts && (user?.role === "Admin" || (a.status === "Disbursed" && !(a.settlements || []).length
        && Date.now() - new Date(a.disbursed_recorded_at || a.disbursed_at).getTime() <= 7 * 86400000))
        && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setCorrectOn(a)} data-testid={`adv-correct-disb-${a.id}`}>Correct disbursement</Button>}
    </div>
  );

  const ownActions = (a) => a.requester_id !== user?.id ? null : a.status === "SentBack"
    ? <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => setEditAdv({ adv: a, mode: "edit" })} data-testid={`adv-edit-btn-${a.id}`}>Edit & resubmit</Button>
    : null;

  const doAction = async (id, action, payload = {}) => {
    let extra = {};
    if (action === "reject") {
      const reason = window.prompt("Reason for rejection (required)");
      if (!reason || !reason.trim()) return;
      extra = { comment: reason };
    }
    try {
      await api.post(`/advances/${id}/${action}`, { comment: "", ...payload, ...extra });
      toast.success(`Marked ${action}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Money · Advances</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Advances & Settlements</h1>
          <p className="text-sm text-slate-500 mt-1">Committed against working line until settled. New advance blocked if any older advance stays unsettled beyond 30 days.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#0F172A]" data-testid="new-advance-btn"><Plus className="w-4 h-4 mr-1" /> Request Advance</Button></DialogTrigger>
          <DialogContent><AdvanceForm projects={projects} payees={payees} onSaved={() => { setOpen(false); load(); }} /></DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="all">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="all">All ({items.length})</TabsTrigger>
          {isApprover && <TabsTrigger value="approve" data-testid="atab-approve">Approve ({items.filter(i => OPEN_ADV.includes(i.status)).length})</TabsTrigger>}
          {isAccounts && <TabsTrigger value="disburse" data-testid="atab-disburse">Disburse ({items.filter(i => i.status === "Approved").length})</TabsTrigger>}
          <TabsTrigger value="settle" data-testid="atab-settle">Settle ({(isAccounts ? openAdv : openAdv.filter(a => a.requester_id === user?.id)).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all"><AdvList items={items} projects={projects} actions={ownActions} me={user?.id} /></TabsContent>
        {isApprover && <TabsContent value="approve">
          <AdvList items={items.filter(i => OPEN_ADV.includes(i.status))} projects={projects} actions={a => (
            <div className="flex gap-2">
              {a.requester_id !== user?.id && <Button size="sm" onClick={() => setTarget({ kind: "advance", id: a.id })} className="bg-emerald-600" data-testid={`adv-approve-${a.id}`}><BadgeCheck className="w-3.5 h-3.5 mr-1" />Review & act</Button>}
            </div>
          )} />
        </TabsContent>}
        {isAccounts && <TabsContent value="disburse">
          <AdvList items={items.filter(i => i.status === "Approved")} projects={projects} actions={a => (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setDisburseOn(a)} data-testid={`adv-disburse-${a.id}`}><CreditCard className="w-3.5 h-3.5 mr-1" />{a.requester_id === user?.id ? "Disburse (own)" : "Disburse"}</Button>
            </div>
          )} />
        </TabsContent>}
        <TabsContent value="settle">
          <AdvList items={isAccounts ? openAdv : openAdv.filter(a => a.requester_id === user?.id)} projects={projects} actions={settleActions} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!disburseOn} onOpenChange={(v) => !v && setDisburseOn(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain">
          {disburseOn && <DisburseForm adv={disburseOn} onSaved={() => { setDisburseOn(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!correctOn} onOpenChange={(v) => !v && setCorrectOn(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overscroll-contain">
          {correctOn && <DisbursementCorrectForm adv={correctOn} onSaved={() => { setCorrectOn(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!settleOn} onOpenChange={(v) => !v && setSettleOn(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain">
          {settleOn && <SettleForm adv={settleOn} onSaved={() => { setSettleOn(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!selfSettle} onOpenChange={(v) => { if (!v) { setSelfSettle(null); window.history.replaceState(null, "", "/app/advances"); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain">
          {selfSettle && <AdvanceSettlementForm adv={selfSettle} onSaved={() => { setSelfSettle(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editAdv} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overscroll-contain" data-testid="adv-edit-dialog">
          {editAdv && <AdvanceEditForm adv={editAdv.adv} mode={editAdv.mode} onSaved={() => { closeEdit(); load(); }} />}
        </DialogContent>
      </Dialog>
      <ChainActionDialog target={target} onClose={() => setTarget(null)} onDone={load} />
    </div>
  );
}

function AdvanceForm({ projects, payees, onSaved }) {
  const [f, setF] = useState({ project_id: "", payee_id: "", purpose: "", amount: 0, expected_settlement_date: "" });
  const [busy, setBusy] = useState(false);
  const inflight = React.useRef(false);
  const submit = async (e) => {
    e.preventDefault();
    if (inflight.current) return;
    inflight.current = true;
    setBusy(true);
    try {
      await api.post("/advances", { ...f, amount: parseFloat(f.amount || 0), payee_id: f.payee_id || null, expected_settlement_date: f.expected_settlement_date || null });
      toast.success("Advance requested");
      onSaved();
    } catch (err) { inflight.current = false; setBusy(false); toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Request Advance</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3 mt-3">
        <div>
          <Label>Project</Label>
          <Select value={f.project_id} onValueChange={(v) => setF({...f, project_id: v})}>
            <SelectTrigger data-testid="adv-project-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Payee (optional — if paying a vendor)</Label>
          <Select value={f.payee_id} onValueChange={(v) => setF({...f, payee_id: v})}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{payees.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Purpose</Label><Textarea required value={f.purpose} onChange={(e) => setF({...f, purpose: e.target.value})} data-testid="adv-purpose-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Amount</Label><Input required type="number" step="0.01" value={f.amount} onChange={(e) => setF({...f, amount: e.target.value})} data-testid="adv-amount-input" /></div>
          <div><Label>Expected Settlement</Label><Input type="date" value={f.expected_settlement_date} onChange={(e) => setF({...f, expected_settlement_date: e.target.value})} /></div>
        </div>
        <DialogFooter><Button type="submit" disabled={busy} data-testid="submit-advance-btn">{busy ? "Saving…" : "Submit"}</Button></DialogFooter>
      </form>
    </>
  );
}


function SettleForm({ adv, onSaved }) {
  const [rmbs, setRmbs] = useState([]);
  const [items, setItems] = useState([]);
  const [refund, setRefund] = useState(0);
  const [refundRef, setRefundRef] = useState("");
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  useEffect(() => {
    api.get(`/reimbursements?project_id=${adv.project_id || ""}`).then(r => setRmbs(r.data.filter(x => x.requester_id === adv.requester_id)));
  }, [adv]);
  const toggle = (r) => {
    const i = items.findIndex(x => x.reimbursement_id === r.id);
    if (i >= 0) setItems(items.filter((_, idx) => idx !== i));
    else setItems([...items, { reimbursement_id: r.id, amount: r.gross_amount || 0, rmb_id: r.rmb_id }]);
  };
  const updateAmt = (rid, amt) => setItems(items.map(x => x.reimbursement_id === rid ? { ...x, amount: parseFloat(amt || 0) } : x));

  const total = items.reduce((s, x) => s + x.amount, 0) + parseFloat(refund || 0);
  const outstanding = (adv.amount || 0) - (adv.settled_amount || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!invoiceFiles.length) {
      toast.error("Attach at least one invoice/receipt to settle this advance");
      return;
    }
    try {
      await api.post(`/advances/${adv.id}/settle`, {
        items: items.map(({ rmb_id, ...rest }) => rest),
        refund_amount: parseFloat(refund || 0),
        refund_reference: refundRef,
        invoice_file_ids: invoiceFiles.map(x => x.id),
      });
      toast.success("Advance settled");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Settle · {adv.advance_id}</DialogTitle></DialogHeader>
      <details className="rounded border p-2" data-testid="settle-details"><summary className="text-sm cursor-pointer">Request details</summary><div className="mt-2"><RequestDetailsLoader kind="advance" id={adv.id} /></div></details>
      <div className="text-sm mt-2 grid grid-cols-3 gap-3">
        <div><div className="text-xs text-slate-500">Advance</div><div className="font-mono font-semibold">₹ {adv.amount?.toLocaleString()}</div></div>
        <div><div className="text-xs text-slate-500">Already settled</div><div className="font-mono">₹ {(adv.settled_amount||0).toLocaleString()}</div></div>
        <div><div className="text-xs text-slate-500">Outstanding</div><div className="font-mono font-bold text-amber-700">₹ {outstanding.toLocaleString()}</div></div>
      </div>
      <form onSubmit={submit} className="space-y-3 mt-4">
        <div>
          <Label>Link bills (reimbursements from same requester on project)</Label>
          <p className="text-[11px] text-slate-500 mb-1">TDS is applied on each bill when Accounts verifies it; the advance is settled against the bill amounts.</p>
          <div className="border rounded-md max-h-48 overflow-y-auto text-sm">
            {rmbs.length === 0 && <div className="p-3 text-slate-500 text-xs">No reimbursements available.</div>}
            {rmbs.map(r => {
              const it = items.find(x => x.reimbursement_id === r.id);
              return (
                <label key={r.id} className="flex items-center gap-2 px-3 py-1.5 border-b hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={!!it} onChange={() => toggle(r)} />
                  <span className="font-mono text-xs">{r.rmb_id}</span>
                  <span className="flex-1 text-xs">{r.payee_name}</span>
                  {it && <Input type="number" step="0.01" value={it.amount} onChange={(e) => updateAmt(r.id, e.target.value)} className="w-24 h-7 text-xs" />}
                  {!it && <span className="text-xs text-slate-500 font-mono">₹ {r.gross_amount?.toLocaleString()}</span>}
                  <span className="text-[10px] text-slate-500" data-testid={`settle-tds-${r.id}`}>{r.tds_confirmed ? `TDS ${r.tds_rate}% · net ₹${(r.payable_amount || 0).toLocaleString()}` : "TDS set at bill Verify"}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Refund (unspent)</Label><Input type="number" step="0.01" value={refund} onChange={(e) => setRefund(e.target.value)} data-testid="adv-refund-input" /></div>
          <div><Label>Refund Ref</Label><Input value={refundRef} onChange={(e) => setRefundRef(e.target.value)} /></div>
        </div>
        <Card className="bg-slate-50"><CardContent className="p-3 text-sm flex justify-between">
          <span>Settling this round: <span className="font-mono font-bold">₹ {total.toLocaleString()}</span></span>
          <span className={total >= outstanding ? "text-emerald-700" : "text-amber-700"}>{total >= outstanding ? "Will close advance" : `₹ ${(outstanding - total).toLocaleString()} will remain outstanding`}</span>
        </CardContent></Card>
        <InvoiceUploader
          label="Invoice / Receipts"
          files={invoiceFiles}
          onChange={setInvoiceFiles}
          required
          testId="settle-invoice"
          hint="Mandatory · PDF, JPG, PNG, WEBP · Max 20 MB each"
        />
        <DialogFooter><Button type="submit" data-testid="save-settle-btn" disabled={!invoiceFiles.length}>Settle</Button></DialogFooter>
      </form>
    </>
  );
}

function AdvList({ items, projects, actions, me }) {
  const projMap = Object.fromEntries(projects.map(p => [p.id, p.name]));
  if (!items.length) return <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">Empty.</CardContent></Card>;
  return (
    <div className="space-y-2">
      {items.map(a => {
        const daysOld = a.disbursed_at ? Math.round((Date.now() - new Date(a.disbursed_at).getTime()) / 86400000) : 0;
        return (
          <Card key={a.id} data-testid={`advance-${a.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{a.advance_id}</span>
                    <span className={`status-chip ${statusColor[a.status]}`}>{a.status}</span>
                    {a.status === "Disbursed" && daysOld > 30 && <Badge variant="destructive" className="text-[10px]">Overdue {daysOld}d</Badge>}
                    {a.self_disbursed && <Badge className="text-[10px] bg-amber-100 text-amber-900 border-amber-300" data-testid={`adv-self-disbursed-${a.id}`}>Self-disbursed (Accounts)</Badge>}
                  </div>
                  {a.disbursed_at && <div className="text-[11px] text-slate-500 font-mono mt-0.5" data-testid={`adv-disb-info-${a.id}`}>Paid {(a.pay_date || a.disbursed_at).slice(0, 10)} · {a.pay_mode || "—"} {a.utr || ""}{a.paid_from_label ? ` · from ${a.paid_from_label}` : ""}{a.disbursed_by_name ? ` · by ${a.disbursed_by_name}` : ""}{a.undisbursed_released > 0 ? ` · ₹${a.undisbursed_released.toLocaleString()} of approved ₹${(a.approved_amount || 0).toLocaleString()} not disbursed (released)` : ""}</div>}
                  <div className="text-sm mt-1 font-medium">{a.purpose}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{a.requester_name} · {projMap[a.project_id] || "—"}</div>
                  {a.status === "SentBack" && <div className="mt-1 text-xs text-rose-800 rounded border border-rose-300 bg-rose-50 px-2 py-1" data-testid={`adv-sentback-${a.id}`}>Sent back by {a.sent_back_by_name}: {a.sent_back_reason}</div>}
                  {a.status === "Rejected" && <div className="mt-1"><RejectedNote doc={a} copyLink={me && a.requester_id === me ? `/app/advances?copy=${a.id}` : null} testId={`adv-rejected-${a.id}`} /></div>}
                  {(() => {
                    const allFiles = (a.settlements || []).flatMap(s => s.invoice_file_ids || []);
                    return allFiles.length > 0 ? <InvoiceLinks fileIds={allFiles} compact /> : null;
                  })()}
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold">₹ {a.amount?.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 font-mono">Outstanding ₹ {(a.outstanding ?? a.amount)?.toLocaleString()}</div>
                </div>
                {actions && actions(a)}
                <ChainButton kind="advance" id={a.id} testId={`adv-chain-${a.id}`} />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
